<template>
  <div class="analytics">
    <div class="analytics__content">
    <!-- Page Header -->
    <div class="analytics__header">
      <div>
        <h3 class="mb-1 mt-1">Innsikt</h3>
        <p class="analytics__subtitle">Dypere innsikt i din restaurant</p>
      </div>
      <div class="analytics__header-right">
        <!-- Location Selector -->
        <div v-if="hasMultiLocations" class="analytics__location-selector">
          <select v-model="selectedLocationId" @change="onLocationChange" class="analytics__select">
            <option value="ALL">Alle avdelinger</option>
            <option v-for="location in locations" :key="location.id" :value="location.id">
              {{ location.name }}
            </option>
          </select>
        </div>
        <!-- Date Range Selector -->
        <div class="analytics__date-range">
          <div class="analytics__date-input">
            <font-awesome-icon icon="calendar-alt" class="analytics__date-icon" />
            <datepicker v-model="fromDateObj" :locale="nbLocale" inputFormat="dd. MMM yyyy" :upperLimit="new Date()" />
          </div>
          <span class="analytics__date-separator">til</span>
          <div class="analytics__date-input">
            <font-awesome-icon icon="calendar-alt" class="analytics__date-icon" />
            <datepicker v-model="toDateObj" :locale="nbLocale" inputFormat="dd. MMM yyyy" :upperLimit="new Date()" />
          </div>
          <button class="analytics__apply-btn" @click="applyDateRange">
            <font-awesome-icon icon="sync-alt" />
            Oppdater
          </button>
        </div>
        <!-- Quick Date Presets -->
        <div class="analytics__presets">
          <button
            v-for="preset in datePresets"
            :key="preset.label"
            :class="['analytics__preset-btn', { 'analytics__preset-btn--active': activePreset === preset.label }]"
            @click="applyPreset(preset)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Error Banner -->
    <div v-if="loadError" class="analytics__error">
      <font-awesome-icon icon="exclamation-triangle" />
      <span>Kunne ikke laste inn data. Vennligst prøv igjen.</span>
      <button class="analytics__error-btn" @click="retryLoad">
        <font-awesome-icon icon="sync-alt" /> Prøv igjen
      </button>
    </div>

    <!-- Growth Metrics Section -->
    <div class="analytics__section">
      <div class="analytics__section-header">
        <h2 class="analytics__section-title">Vekstmålinger</h2>
        <span class="analytics__section-subtitle">Sammenlign perioder og spor vekst</span>
      </div>
      <div class="analytics__growth-grid">
        <GrowthMetricCard
          period="Denne uken vs forrige"
          currentLabel="Denne uken"
          previousLabel="Forrige uke"
          :currentValue="growthMetrics.weekCurrent"
          :previousValue="growthMetrics.weekPrevious"
          :growth="growthMetrics.weekGrowth"
          :loading="loadingGrowth"
        />
        <GrowthMetricCard
          period="Denne måneden vs forrige"
          currentLabel="Denne måneden"
          previousLabel="Forrige måned"
          :currentValue="growthMetrics.monthCurrent"
          :previousValue="growthMetrics.monthPrevious"
          :growth="growthMetrics.monthGrowth"
          :loading="loadingGrowth"
        />
        <GrowthMetricCard
          period="Dette året vs i fjor"
          currentLabel="Dette året"
          previousLabel="I fjor"
          :currentValue="growthMetrics.yearCurrent"
          :previousValue="growthMetrics.yearPrevious"
          :growth="growthMetrics.yearGrowth"
          :loading="loadingGrowth"
        />
      </div>
    </div>

    <!-- Time Patterns Section -->
    <div class="analytics__section">
      <div class="analytics__section-header">
        <h2 class="analytics__section-title">Tidsmønstre</h2>
        <span class="analytics__section-subtitle">Forstå når kundene handler</span>
      </div>
      <div class="analytics__time-grid">
        <div class="analytics__chart-col">
          <InlineSpinner v-if="loadingTimePatterns" />
          <DayOfWeekChart
            v-else
            title="Salg per ukedag"
            subtitle="Gjennomsnittlig omsetning"
            :labels="dayOfWeekLabels"
            :data="dayOfWeekData"
          />
        </div>
        <div class="analytics__chart-col">
          <InlineSpinner v-if="loadingTimePatterns" />
          <HourlyHeatmapChart
            v-else
            title="Salgsvarme"
            subtitle="Omsetning per time og ukedag"
            :data="hourlyHeatmapData"
          />
        </div>
      </div>
    </div>

    <!-- Product Performance Section -->
    <div class="analytics__section">
      <div class="analytics__section-header">
        <h2 class="analytics__section-title">Produktytelse</h2>
        <span class="analytics__section-subtitle">Se hvilke produkter som driver omsetningen</span>
      </div>
      <div class="analytics__products-grid">
        <div class="analytics__chart-col">
          <InlineSpinner v-if="loadingProducts" />
          <TopProductsChart
            v-else
            :title="productChartTitle"
            :subtitle="productChartSubtitle"
            :products="displayedProducts"
            :tabs="['Topp 10', 'Bunn 10']"
            :activeTab="productTab"
            @tab-change="productTab = $event"
          />
        </div>
        <div class="analytics__chart-col">
          <InlineSpinner v-if="loadingCategories" />
          <CategoryPerformanceChart
            v-else
            title="Kategoriytelse"
            subtitle="Omsetning per kategori"
            :categories="categoryData"
          />
        </div>
      </div>
    </div>

    <!-- Sales Distribution Section -->
    <div class="analytics__section">
      <div class="analytics__section-header">
        <h2 class="analytics__section-title">Salgsfordeling</h2>
        <span class="analytics__section-subtitle">Se hvor salget kommer fra</span>
      </div>
      <div class="analytics__distribution-grid">
        <div class="analytics__chart-col">
          <InlineSpinner v-if="loadingChannels" />
          <SalesDonutChart
            v-else
            title="Salg per kanal"
            :labels="channelLabels"
            :series="channelData"
            :colors="['#B30E10', '#4ECDC4', '#45B7D1', '#FF6B6B', '#96CEB4']"
          />
        </div>
        <div class="analytics__chart-col">
          <InlineSpinner v-if="loadingPaymentMethods" />
          <SalesDonutChart
            v-else
            title="Salg per betalingsmetode"
            :labels="paymentMethodLabels"
            :series="paymentMethodData"
            :colors="['#FF6B6B', '#4ECDC4', '#96CEB4', '#45B7D1', '#DDA0DD', '#F0E68C']"
          />
        </div>
      </div>
    </div>

    <!-- Key Metrics Summary -->
    <div class="analytics__section">
      <div class="analytics__section-header">
        <h2 class="analytics__section-title">Nøkkeltall for perioden</h2>
        <span class="analytics__section-subtitle">{{ formatDate(fromDateObj) }} - {{ formatDate(toDateObj) }}</span>
      </div>
      <div class="analytics__metrics-grid">
        <div class="analytics__metric-card">
          <div class="analytics__metric-icon" style="background-color: rgba(179, 14, 16, 0.1);">
            <font-awesome-icon icon="coins" style="color: #B30E10;" />
          </div>
          <div class="analytics__metric-content">
            <span class="analytics__metric-value">{{ formatCurrency(summaryMetrics.totalRevenue) }}</span>
            <span class="analytics__metric-label">Total omsetning</span>
          </div>
        </div>
        <div class="analytics__metric-card">
          <div class="analytics__metric-icon" style="background-color: rgba(78, 205, 196, 0.1);">
            <font-awesome-icon icon="shopping-bag" style="color: #4ECDC4;" />
          </div>
          <div class="analytics__metric-content">
            <span class="analytics__metric-value">{{ formatNumber(summaryMetrics.totalOrders) }}</span>
            <span class="analytics__metric-label">Antall ordrer</span>
          </div>
        </div>
        <div class="analytics__metric-card">
          <div class="analytics__metric-icon" style="background-color: rgba(255, 107, 107, 0.1);">
            <font-awesome-icon icon="receipt" style="color: #FF6B6B;" />
          </div>
          <div class="analytics__metric-content">
            <span class="analytics__metric-value">{{ formatCurrency(summaryMetrics.averageOrderValue) }}</span>
            <span class="analytics__metric-label">Gj.snitt ordreverdi</span>
          </div>
        </div>
        <div class="analytics__metric-card">
          <div class="analytics__metric-icon" style="background-color: rgba(69, 183, 209, 0.1);">
            <font-awesome-icon icon="chart-line" style="color: #45B7D1;" />
          </div>
          <div class="analytics__metric-content">
            <span class="analytics__metric-value">{{ formatCurrency(summaryMetrics.dailyAverage) }}</span>
            <span class="analytics__metric-label">Daglig gjennomsnitt</span>
          </div>
        </div>
        <div class="analytics__metric-card">
          <div class="analytics__metric-icon" style="background-color: rgba(150, 206, 180, 0.1);">
            <font-awesome-icon icon="clock" style="color: #96CEB4;" />
          </div>
          <div class="analytics__metric-content">
            <span class="analytics__metric-value">{{ summaryMetrics.peakHour || '--:--' }}</span>
            <span class="analytics__metric-label">Peak time</span>
          </div>
        </div>
        <div class="analytics__metric-card">
          <div class="analytics__metric-icon" style="background-color: rgba(221, 160, 221, 0.1);">
            <font-awesome-icon icon="calendar-day" style="color: #DDA0DD;" />
          </div>
          <div class="analytics__metric-content">
            <span class="analytics__metric-value">{{ summaryMetrics.bestDay || '--' }}</span>
            <span class="analytics__metric-label">Beste ukedag</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import InlineSpinner from "@/components/other/InlineSpinner";
import GrowthMetricCard from "@/components/analytics/GrowthMetricCard";
import TopProductsChart from "@/components/analytics/TopProductsChart";
import DayOfWeekChart from "@/components/analytics/DayOfWeekChart";
import HourlyHeatmapChart from "@/components/analytics/HourlyHeatmapChart";
import CategoryPerformanceChart from "@/components/analytics/CategoryPerformanceChart";
import SalesDonutChart from "@/components/dashboard/SalesDonutChart";
import AnalyticsService from "@/services/analytics/AnalyticsService";
import DashboardService from "@/services/dashboard/DashboardService";
import LocationUtil from "@/util/LocationUtil";
import moment from "moment/min/moment-with-locales";
import Datepicker from '@/components/other/ToggleDatepicker.vue';
import { nb } from 'date-fns/locale';

moment.locale("nb");

export default {
  name: 'BusinessAnalytics',
  components: {
    InlineSpinner,
    GrowthMetricCard,
    TopProductsChart,
    DayOfWeekChart,
    HourlyHeatmapChart,
    CategoryPerformanceChart,
    SalesDonutChart,
    Datepicker
  },
  data() {
    return {
      // Location
      selectedLocationId: localStorage.getItem('analyticsLocationId') || 'ALL',
      locations: [],

      // Date range
      fromDateObj: new Date(new Date().setDate(new Date().getDate() - 30)),
      toDateObj: new Date(),
      nbLocale: nb,
      activePreset: 'Siste 30 dager',

      // Date presets
      datePresets: [
        { label: 'Siste 7 dager', days: 7 },
        { label: 'Siste 30 dager', days: 30 },
        { label: 'Siste 90 dager', days: 90 },
        { label: 'Dette året', days: 'ytd' }
      ],

      // Loading states
      loadingGrowth: true,
      loadingProducts: true,
      loadingCategories: true,
      loadingTimePatterns: true,
      loadingChannels: true,
      loadingPaymentMethods: true,

      // Error state
      loadError: false,

      // Product tab
      productTab: 0,

      // Growth metrics
      growthMetrics: {
        weekCurrent: 0,
        weekPrevious: 0,
        weekGrowth: null,
        monthCurrent: 0,
        monthPrevious: 0,
        monthGrowth: null,
        yearCurrent: 0,
        yearPrevious: 0,
        yearGrowth: null
      },

      // Product data
      topProducts: [],
      bottomProducts: [],

      // Category data
      categoryData: [],

      // Channel data (Salg per kanal)
      channelLabels: [],
      channelData: [],

      // Payment method data (Salg per betalingsmetode)
      paymentMethodLabels: [],
      paymentMethodData: [],

      // Time patterns
      dayOfWeekLabels: ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'],
      dayOfWeekData: [],
      hourlyHeatmapData: [],

      // Summary metrics
      summaryMetrics: {
        totalRevenue: 0,
        totalOrders: 0,
        averageOrderValue: 0,
        dailyAverage: 0,
        peakHour: null,
        bestDay: null
      }
    };
  },
  computed: {
    hasMultiLocations() {
      return LocationUtil.hasMultiLocations();
    },
    fromDate() {
      return moment(this.fromDateObj).format('YYYY-MM-DD');
    },
    toDate() {
      return moment(this.toDateObj).format('YYYY-MM-DD');
    },
    displayedProducts() {
      return this.productTab === 0 ? this.topProducts : this.bottomProducts;
    },
    productChartTitle() {
      return this.productTab === 0 ? 'Topp 10 produkter' : 'Bunn 10 produkter';
    },
    productChartSubtitle() {
      return this.productTab === 0 ? 'Høyest omsetning' : 'Lavest omsetning';
    }
  },
  watch: {
    selectedLocationId(newVal) {
      if (newVal) {
        localStorage.setItem('analyticsLocationId', newVal);
      }
    }
  },
  async created() {
    window.__analyticsLoading = true;
    await this.loadLocations();
    this.loadAllData();
  },
  methods: {
    async loadLocations() {
      try {
        const response = await DashboardService.getBasicData();
        this.locations = response.data.locations || [];
        // Validate stored locationId - reset to ALL if it no longer exists
        if (this.selectedLocationId !== 'ALL' && this.locations.length > 0) {
          const locationExists = this.locations.some(l => String(l.id) === String(this.selectedLocationId));
          if (!locationExists) {
            this.selectedLocationId = 'ALL';
          }
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    },
    async loadAllData() {
      this.loadError = false;
      try {
        const results = await Promise.allSettled([
          this.loadGrowthMetrics(),
          this.loadProductPerformance(),
          this.loadCategoryAnalytics(),
          this.loadTimePatterns(),
          this.loadChannelAnalytics(),
          this.loadPaymentMethodAnalytics()
        ]);
        const allFailed = results.every(r => r.status === 'rejected' || r.value === false);
        if (allFailed) {
          this.loadError = true;
        }
      } finally {
        // Signal to App.vue nav that loading is done
        window.__analyticsLoading = false;
      }
    },
    retryLoad() {
      this.loadAllData();
    },
    async loadGrowthMetrics() {
      this.loadingGrowth = true;
      try {
        const response = await AnalyticsService.getGrowthMetrics(this.selectedLocationId);
        const data = response.data;
        this.growthMetrics = {
          weekCurrent: data.weekCurrent || 0,
          weekPrevious: data.weekPrevious || 0,
          weekGrowth: data.weekGrowth,
          monthCurrent: data.monthCurrent || 0,
          monthPrevious: data.monthPrevious || 0,
          monthGrowth: data.monthGrowth,
          yearCurrent: data.yearCurrent || 0,
          yearPrevious: data.yearPrevious || 0,
          yearGrowth: data.yearGrowth
        };
        this.loadingGrowth = false;
        return true;
      } catch (error) {
        console.error('Error loading growth metrics:', error);
        this.loadingGrowth = false;
        return false;
      }
    },
    async loadProductPerformance() {
      this.loadingProducts = true;
      try {
        const response = await AnalyticsService.getProductPerformance(this.fromDate, this.toDate, this.selectedLocationId);
        const data = response.data;
        this.topProducts = data.topProducts || [];
        this.bottomProducts = data.bottomProducts || [];
        this.updateSummaryMetrics(data);
        this.loadingProducts = false;
        return true;
      } catch (error) {
        console.error('Error loading product performance:', error);
        this.loadingProducts = false;
        return false;
      }
    },
    async loadCategoryAnalytics() {
      this.loadingCategories = true;
      try {
        const response = await AnalyticsService.getCategoryAnalytics(this.fromDate, this.toDate, this.selectedLocationId);
        this.categoryData = response.data.categories || [];
        this.loadingCategories = false;
        return true;
      } catch (error) {
        console.error('Error loading category analytics:', error);
        this.loadingCategories = false;
        return false;
      }
    },
    async loadTimePatterns() {
      this.loadingTimePatterns = true;
      try {
        const response = await AnalyticsService.getTimePatternsAnalytics(this.fromDate, this.toDate, this.selectedLocationId);
        const data = response.data;
        this.dayOfWeekData = data.dayOfWeekData || [];
        this.hourlyHeatmapData = data.hourlyHeatmap || [];
        this.updateTimeSummary(data);
        this.loadingTimePatterns = false;
        return true;
      } catch (error) {
        console.error('Error loading time patterns:', error);
        this.loadingTimePatterns = false;
        return false;
      }
    },
    async loadChannelAnalytics() {
      this.loadingChannels = true;
      try {
        const response = await AnalyticsService.getSalesByChannel(this.fromDate, this.toDate, this.selectedLocationId);
        const channels = response.data.channels || [];
        this.channelLabels = channels.map(c => c.name);
        this.channelData = channels.map(c => c.sales);
        this.loadingChannels = false;
        return true;
      } catch (error) {
        console.error('Error loading channel analytics:', error);
        this.loadingChannels = false;
        return false;
      }
    },
    async loadPaymentMethodAnalytics() {
      this.loadingPaymentMethods = true;
      try {
        const response = await AnalyticsService.getSalesByPaymentMethod(this.fromDate, this.toDate, this.selectedLocationId);
        const paymentMethods = response.data.paymentMethods || [];
        this.paymentMethodLabels = paymentMethods.map(p => p.name);
        this.paymentMethodData = paymentMethods.map(p => p.sales);
        this.loadingPaymentMethods = false;
        return true;
      } catch (error) {
        console.error('Error loading payment method analytics:', error);
        this.loadingPaymentMethods = false;
        return false;
      }
    },
    updateSummaryMetrics(data) {
      if (data.summary) {
        this.summaryMetrics.totalRevenue = data.summary.totalRevenue || 0;
        this.summaryMetrics.totalOrders = data.summary.totalOrders || 0;
        this.summaryMetrics.averageOrderValue = data.summary.averageOrderValue || 0;
        this.summaryMetrics.dailyAverage = data.summary.dailyAverage || 0;
      }
    },
    updateTimeSummary(data) {
      if (data.peakHour) {
        this.summaryMetrics.peakHour = data.peakHour;
      }
      if (data.bestDay) {
        this.summaryMetrics.bestDay = data.bestDay;
      }
    },
    applyDateRange() {
      this.activePreset = null;
      this.loadAllData();
    },
    applyPreset(preset) {
      this.activePreset = preset.label;
      if (preset.days === 'ytd') {
        this.fromDateObj = new Date(new Date().getFullYear(), 0, 1);
      } else {
        this.fromDateObj = new Date(new Date().setDate(new Date().getDate() - preset.days));
      }
      this.toDateObj = new Date();
      this.loadAllData();
    },
    onLocationChange() {
      this.loadAllData();
    },
    formatDate(date) {
      return moment(date).format('D. MMM YYYY');
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('nb-NO').format(Math.round(value)) + ' kr';
    },
    formatNumber(value) {
      return new Intl.NumberFormat('nb-NO').format(Math.round(value));
    }
  }
};
</script>

<style scoped>
.analytics {
  padding: 24px;
  background-color: var(--bo-surface-2);
  min-height: 100vh;
}

.analytics__content {
  max-width: 1860px;
  margin: 0 auto;
}

.analytics__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.analytics__subtitle {
  font-size: 14px;
  color: var(--bo-muted);
  margin: 0;
}

.analytics__header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.analytics__location-selector {
  position: relative;
}

.analytics__select {
  padding: 10px 36px 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--bo-ink);
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  min-width: 180px;
}

.analytics__select:focus {
  outline: none;
  border-color: #B30E10;
  box-shadow: 0 0 0 3px rgba(179, 14, 16, 0.1);
}

.analytics__date-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.analytics__date-input {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bo-surface);
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--bo-line);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.analytics__date-input:hover {
  border-color: #B30E10;
}

.analytics__date-input :deep(input) {
  border: none;
  font-size: 14px;
  color: var(--bo-ink);
  background: transparent;
  cursor: pointer;
  width: 110px;
  padding: 0;
}

.analytics__date-input :deep(input):focus {
  outline: none;
}

.analytics__date-icon {
  color: var(--bo-alert-ink);
  font-size: 14px;
}

.analytics__date-separator {
  color: var(--bo-muted);
  font-size: 14px;
}

.analytics__apply-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  background: #B30E10;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.analytics__apply-btn:hover {
  background: #8c0b0d;
}

.analytics__presets {
  display: flex;
  gap: 4px;
  background: var(--bo-surface-2);
  padding: 4px;
  border-radius: 8px;
}

.analytics__preset-btn {
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--bo-muted);
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.analytics__preset-btn:hover {
  color: var(--bo-ink);
}

.analytics__preset-btn--active {
  background: var(--bo-surface);
  color: var(--bo-alert-ink);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.analytics__error {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: var(--bo-bad-bg);
  border: 1px solid var(--bo-bad-line);
  border-radius: 12px;
  color: var(--bo-alert-ink);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
}

.analytics__error-btn {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #B30E10;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}

.analytics__error-btn:hover {
  background: #8c0b0d;
}

.analytics__section {
  margin-bottom: 32px;
}

.analytics__section-header {
  margin-bottom: 16px;
}

.analytics__section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0 0 4px 0;
}

.analytics__section-subtitle {
  font-size: 13px;
  color: var(--bo-muted);
}

.analytics__growth-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.analytics__time-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 20px;
}

.analytics__products-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 20px;
}

.analytics__distribution-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.analytics__chart-col {
  min-height: 300px;
  position: relative;
}

.analytics__metrics-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
}

.analytics__metric-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bo-surface);
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.analytics__metric-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 18px;
}

.analytics__metric-content {
  display: flex;
  flex-direction: column;
}

.analytics__metric-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--bo-ink);
}

.analytics__metric-label {
  font-size: 12px;
  color: var(--bo-muted);
}

/* Responsive */
@media (max-width: 1400px) {
  .analytics__metrics-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .analytics__growth-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .analytics__time-grid,
  .analytics__products-grid,
  .analytics__distribution-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .analytics__metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .analytics__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .analytics__header-right {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }

  .analytics__date-range {
    flex-wrap: wrap;
  }

  .analytics__presets {
    width: 100%;
    overflow-x: auto;
  }
}

@media (max-width: 768px) {
  .analytics {
    padding: 16px;
  }

  .analytics__growth-grid {
    grid-template-columns: 1fr;
  }

  .analytics__metrics-grid {
    grid-template-columns: 1fr;
  }

  .analytics__select {
    width: 100%;
  }
}
</style>
